import { ActionIcon, Group, Menu, Select, SelectItemProps, Text as MantineText } from '@mantine/core';
import { IconCheck } from '@tabler/icons';
import { FC, forwardRef, useContext } from 'react';
import { Language } from '../../Service/DocumentT';
import { ViolationContext } from '../../Service/Context/ViolationContext';
import { useJwtClaims } from '../../Service/Context/AuthContext';
import { LicenceS } from '../../Service/LicenceS';

const languageOptions: { flag: string; label: string; value: Language }[] = [
  { flag: '🇩🇪', label: 'Deutsch', value: 'german' },
  { flag: '🇬🇧', label: 'Englisch', value: 'english' },
];

interface LanguageSelectItemProps extends SelectItemProps {
  flag: string;
}

const LanguageSelectItem = forwardRef<HTMLDivElement, LanguageSelectItemProps>(
  ({ flag, label, value, ...rest }: LanguageSelectItemProps, ref) => (
    // eslint-disable-next-line react/jsx-props-no-spreading
    <div ref={ref} data-value={value} {...rest}>
      <Group spacing="xs" noWrap>
        <MantineText component="span" size="lg" sx={{ lineHeight: 1 }}>
          {flag}
        </MantineText>
        <MantineText>{label}</MantineText>
      </Group>
    </div>
  ),
);

const DocumentLanguagePicker: FC<{
  language?: Language | null;
  onLanguageChange?: (language: Language) => void;
  variant?: 'menu' | 'menu-items' | 'select';
}> = ({ language = 'german', onLanguageChange, variant = 'menu' }) => {
  const { licence } = useJwtClaims();
  const { openLicenceModal } = useContext(ViolationContext);
  const documentLanguage = language ?? 'german';
  const languageFlag =
    languageOptions.find((option) => option.value === documentLanguage)?.flag ?? languageOptions[0].flag;

  const changeLanguage = (newLanguage: Language) => {
    if (newLanguage === documentLanguage || !onLanguageChange) {
      return;
    }
    const restriction = LicenceS.Restrictions.DOCUMENT_LANGUAGE;
    if (!LicenceS.hasSuitableLicence(restriction.requiredLicence, licence)) {
      openLicenceModal(restriction);
      return;
    }
    onLanguageChange(newLanguage);
  };

  if (variant === 'select') {
    return (
      <Select
        label="Dokumentsprache"
        value={documentLanguage}
        data={languageOptions}
        itemComponent={LanguageSelectItem}
        icon={
          <MantineText component="span" size="lg" sx={{ lineHeight: 1 }}>
            {languageFlag}
          </MantineText>
        }
        disabled={!onLanguageChange}
        sx={{ maxWidth: 250 }}
        onChange={(value) => {
          const selectedLanguage = languageOptions.find((option) => option.value === value)?.value;
          if (selectedLanguage) {
            changeLanguage(selectedLanguage);
          }
        }}
      />
    );
  }

  const menuItems = (
    <>
      <Menu.Label>Dokumentsprache</Menu.Label>
      {languageOptions.map((option) => (
        <Menu.Item
          key={option.value}
          icon={
            <MantineText component="span" size="lg" sx={{ lineHeight: 1 }}>
              {option.flag}
            </MantineText>
          }
          rightSection={option.value === documentLanguage ? <IconCheck size={16} /> : undefined}
          onClick={() => changeLanguage(option.value)}
        >
          {option.label}
        </Menu.Item>
      ))}
    </>
  );

  if (variant === 'menu-items') {
    return menuItems;
  }

  return (
    <Menu shadow="md" width={180} transition="scale-y" position="bottom-end">
      <Menu.Target>
        <ActionIcon
          size={30}
          color="blue"
          variant="transparent"
          disabled={!onLanguageChange}
          title="Dokumentsprache ändern"
          aria-label="Dokumentsprache ändern"
        >
          <MantineText component="span" size="lg" sx={{ lineHeight: 1 }}>
            {languageFlag}
          </MantineText>
        </ActionIcon>
      </Menu.Target>
      <Menu.Dropdown>{menuItems}</Menu.Dropdown>
    </Menu>
  );
};

export default DocumentLanguagePicker;
